import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ArchiveRestore,
    Bold,
    Check,
    CheckSquare,
    ChevronRight,
    Code,
    Edit3,
    Eraser,
    Eye,
    Folder as FolderIcon,
    Grid2X2,
    Hash,
    Italic,
    List,
    ListTodo,
    Loader2,
    Copy,
    MessageSquareText,
    MoreHorizontal,
    NotebookTabs,
    Pin,
    Quote,
    Redo2,
    Save,
    ScrollText,
    Send,
    Share2,
    ShieldCheck,
    Sparkles,
    Strikethrough,
    Tags,
    Trash2,
    Type,
    Underline,
    Undo2,
    UsersRound,
    X,
    type LucideIcon,
} from "lucide-react";
import {
    autosaveNote,
    fetchNoteVersions,
    restoreNoteVersion,
    shareNote,
    togglePin,
    toggleTrash,
    unshareNote,
    updateNote,
    type CreateNoteInput,
    type Folder as FolderType,
    type Note,
    type NoteVersion,
} from "../../api/notes";
import { searchProfiles, type ProfileSearchUser } from "../../api/profile";
import { fetchJoinedSpaces, shareNoteToSpace, type Space as SpaceType, type SpaceMaterial } from "../../api/spaces";
import {
    clearOpenNoteDraft,
    readOpenNoteDraft,
    writeOpenNoteDraft,
    type ContentView,
    type NoteType,
    type SidebarFilter,
    type ViewMode,
} from "./vaultState";

export const PAGE_SIZE = 24;

export const OPEN_COLORS = ["#22d3ee", "#a78bfa", "#34d399", "#f59e0b", "#fb7185", "#60a5fa", "#f472b6", "#c084fc"];

export const FOLDER_COLORS = [
    { hex: "#a855f7", label: "Purple" },
    { hex: "#06b6d4", label: "Cyan" },
    { hex: "#f43f5e", label: "Rose" },
    { hex: "#f59e0b", label: "Amber" },
    { hex: "#10b981", label: "Emerald" },
    { hex: "#3b82f6", label: "Blue" },
    { hex: "#ec4899", label: "Pink" },
    { hex: "#64748b", label: "Slate" },
];

export const NOTE_TYPES: Array<{
    id: NoteType;
    label: string;
    description: string;
    icon: LucideIcon;
    accent: string;
}> = [
    {
        id: "text",
        label: "Note",
        description: "Clean thoughts",
        icon: ScrollText,
        accent: "text-cyan-300",
    },
    {
        id: "list",
        label: "Checklist",
        description: "Steps and tasks",
        icon: ListTodo,
        accent: "text-emerald-300",
    },
    {
        id: "code",
        label: "Code",
        description: "Snippets and fixes",
        icon: Code,
        accent: "text-blue-300",
    },
    {
        id: "rich",
        label: "Journal",
        description: "Styled writing",
        icon: Sparkles,
        accent: "text-pink-300",
    },
];

export const CONTENT_VIEW_OPTIONS: Array<{ id: ContentView; label: string; icon: LucideIcon; iconClassName: string }> = [
    { id: "both", label: "Both", icon: Grid2X2, iconClassName: "text-cyan-300" },
    { id: "notes", label: "Notes", icon: ScrollText, iconClassName: "text-cyan-300" },
    { id: "folders", label: "Folders", icon: FolderIcon, iconClassName: "text-cyan-300" },
];

export const QUICK_FILTERS: Array<{
    id: SidebarFilter;
    label: string;
    icon: LucideIcon;
    iconClassName: string;
    activeClassName: string;
}> = [
    { id: "all", label: "All notes", icon: NotebookTabs, iconClassName: "text-sky-300", activeClassName: "border-transparent text-sky-100" },
    { id: "favourites", label: "Pinned", icon: Pin, iconClassName: "text-amber-300", activeClassName: "border-transparent text-amber-100" },
    { id: "shared", label: "Shared", icon: UsersRound, iconClassName: "text-violet-300", activeClassName: "border-transparent text-violet-100" },
    { id: "recycle-bin", label: "Recycle Bin", icon: Trash2, iconClassName: "text-rose-300", activeClassName: "border-transparent text-rose-100" },
];

export const MATERIAL_KINDS: Array<{ id: SpaceMaterial["kind"]; label: string; icon: LucideIcon }> = [
    { id: "material", label: "Material", icon: ScrollText },
    { id: "assignment", label: "Assignment", icon: CheckSquare },
    { id: "reference", label: "Reference", icon: Quote },
    { id: "lecture", label: "Lecture Note", icon: NotebookTabs },
    { id: "discussion", label: "Discussion Seed", icon: MessageSquareText },
];

const NumberedListIcon = (({ size = 15, ...props }: any) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        {...props}
    >
        <path d="M10 6h10" />
        <path d="M10 12h10" />
        <path d="M10 18h10" />
        <path d="M4 5h1v4" />
        <path d="M3.5 9h3" />
        <path d="M3.5 12.5h2.2c.7 0 1.1.8.6 1.3L4 16h2.5" />
        <path d="M4 19h1.2c.7 0 1.1-.8.6-1.3L5 17" />
    </svg>
)) as LucideIcon;

export function timeAgo(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const sec = Math.max(0, Math.floor((now - then) / 1000));
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return `${Math.floor(day / 7)}w ago`;
}

export function isFolderFilter(filter: SidebarFilter) {
    return !["all", "favourites", "shared", "recycle-bin", "unfiled"].includes(filter);
}

export function descendantFolderIds(folders: FolderType[], folderId: string) {
    const childrenByParent = new Map<string, FolderType[]>();
    folders.forEach((folder) => {
        if (!folder.parent) return;
        childrenByParent.set(folder.parent, [...(childrenByParent.get(folder.parent) || []), folder]);
    });
    const ids = new Set<string>();
    const visit = (id: string) => {
        (childrenByParent.get(id) || []).forEach((child) => {
            if (ids.has(child.id)) return;
            ids.add(child.id);
            visit(child.id);
        });
    };
    visit(folderId);
    return ids;
}

export function notePreview(content: string) {
    if (!content) return "";
    if (typeof document === "undefined") return content.replace(/<[^>]*>/g, " ");
    const element = document.createElement("div");
    element.innerHTML = content;
    return (element.textContent || element.innerText || "").replace(/\s+/g, " ").trim();
}

export function normalizeVaultColor(value: string | null | undefined, fallback = "#22d3ee") {
    const color = String(value || "").trim();
    return /^#[0-9a-f]{6}$/i.test(color) ? color.toLowerCase() : fallback;
}

function sanitizeRichHtml(html: string) {
    if (!html) return "";
    const template = document.createElement("template");
    const scriptProtocol = ["java", "script:"].join("");
    template.innerHTML = html;
    template.content.querySelectorAll("script, style, iframe, object, embed").forEach((node) => node.remove());
    template.content.querySelectorAll("*").forEach((node) => {
        Array.from(node.attributes).forEach((attr) => {
            const name = attr.name.toLowerCase();
            const value = attr.value.toLowerCase();
            if (name.startsWith("on") || value.includes(scriptProtocol)) node.removeAttribute(attr.name);
        });
    });
    return template.innerHTML;
}

export function rememberColor(hex: string) {
    const normalized = normalizeVaultColor(hex);
    const existing = JSON.parse(localStorage.getItem("vaultRecentColors") || "[]") as string[];
    const next = [normalized, ...existing.filter((item) => item !== normalized)].slice(0, 10);
    localStorage.setItem("vaultRecentColors", JSON.stringify(next));
    return next;
}

export function normalizeUsernameSearch(value: string) {
    return value.trim().replace(/^@+/, "");
}

function noteTypeMeta(type: NoteType) {
    return NOTE_TYPES.find((item) => item.id === type) || NOTE_TYPES[0];
}

function ToolbarButton({
    title,
    icon: Icon,
    onClick,
    active = false,
    disabled = false,
}: {
    title: string;
    icon: LucideIcon;
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            disabled={disabled}
            className={`bs-vault-toolbar-button flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition-colors ${
                active
                    ? "border-cyan-700 bg-cyan-950/60 text-cyan-200"
                    : "border-neutral-800 bg-neutral-900 text-neutral-400 hover:border-neutral-700 hover:text-white"
            } disabled:cursor-not-allowed disabled:opacity-35`}
        >
            <Icon size={15} />
        </button>
    );
}

export function RichEditor({
    value,
    onChange,
    placeholder,
    rows,
    historyKey,
    className = "",
}: {
    value: string;
    onChange: (next: string) => void;
    placeholder: string;
    rows: number;
    historyKey: string;
    className?: string;
}) {
    const editorRef = useRef<HTMLDivElement | null>(null);
    const [activeCommands, setActiveCommands] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value || "";
        }
    }, [historyKey, value]);

    const syncValue = useCallback(() => {
        const html = sanitizeRichHtml(editorRef.current?.innerHTML || "");
        onChange(html);
    }, [onChange]);

    const refreshActive = useCallback(() => {
        const commands = ["bold", "italic", "underline", "strikeThrough", "insertUnorderedList", "insertOrderedList"];
        const next: Record<string, boolean> = {};
        commands.forEach((command) => {
            try {
                next[command] = document.queryCommandState(command);
            } catch {
                next[command] = false;
            }
        });
        setActiveCommands(next);
    }, []);

    const exec = (command: string, valueArg?: string) => {
        editorRef.current?.focus();
        document.execCommand(command, false, valueArg);
        syncValue();
        refreshActive();
    };

    const insertChecklist = () => {
        editorRef.current?.focus();
        document.execCommand("insertHTML", false, "<div><label><input type=\"checkbox\" /> Task</label></div>");
        syncValue();
    };

    return (
        <div className={`bs-vault-rich-shell overflow-hidden rounded-xl border ${className}`}>
            <div className="bs-vault-editor-toolbar hidden-scrollbar flex flex-nowrap items-center gap-1 border-b p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <ToolbarButton title="Undo" icon={Undo2} onClick={() => exec("undo")} />
                <ToolbarButton title="Redo" icon={Redo2} onClick={() => exec("redo")} />
                <div className="bs-vault-toolbar-divider mx-1 h-5 w-px" />
                <ToolbarButton title="Bold" icon={Bold} onClick={() => exec("bold")} active={activeCommands.bold} />
                <ToolbarButton title="Italic" icon={Italic} onClick={() => exec("italic")} active={activeCommands.italic} />
                <ToolbarButton title="Underline" icon={Underline} onClick={() => exec("underline")} active={activeCommands.underline} />
                <ToolbarButton title="Strikethrough" icon={Strikethrough} onClick={() => exec("strikeThrough")} active={activeCommands.strikeThrough} />
                <ToolbarButton title="Inline code" icon={Code} onClick={() => exec("fontName", "monospace")} />
                <div className="bs-vault-toolbar-divider mx-1 h-5 w-px" />
                <ToolbarButton title="Heading" icon={Type} onClick={() => exec("formatBlock", "h2")} />
                <ToolbarButton title="Quote" icon={Quote} onClick={() => exec("formatBlock", "blockquote")} />
                <ToolbarButton title="Bullet list" icon={List} onClick={() => exec("insertUnorderedList")} active={activeCommands.insertUnorderedList} />
                <ToolbarButton title="Numbered list" icon={NumberedListIcon} onClick={() => exec("insertOrderedList")} active={activeCommands.insertOrderedList} />
                <ToolbarButton title="Checklist" icon={CheckSquare} onClick={insertChecklist} />
                <ToolbarButton title="Code block" icon={Code} onClick={() => exec("formatBlock", "pre")} />
                <div className="bs-vault-toolbar-divider mx-1 h-5 w-px" />
                <ToolbarButton title="Clear formatting" icon={Eraser} onClick={() => exec("removeFormat")} />
            </div>
            <div
                ref={editorRef}
                contentEditable
                role="textbox"
                aria-label={placeholder}
                data-placeholder={placeholder}
                onInput={syncValue}
                onKeyUp={refreshActive}
                onMouseUp={refreshActive}
                suppressContentEditableWarning
                style={{ minHeight: `${rows * 1.5}rem` }}
                className="bs-vault-rich-editor vault-rich-editor w-full px-4 py-4 text-sm leading-7 text-neutral-100 outline-none empty:before:pointer-events-none empty:before:text-neutral-600 empty:before:content-[attr(data-placeholder)]"
            />
        </div>
    );
}

export function TypeSelector({
    value,
    onChange,
}: {
    value: NoteType;
    onChange: (next: NoteType) => void;
}) {
    return (
        <div className="bs-vault-type-selector grid grid-cols-2 gap-2 sm:grid-cols-4">
            {NOTE_TYPES.map(({ id, label, description, icon: Icon, accent }) => {
                const active = value === id;
                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => onChange(id)}
                        aria-pressed={active}
                        className={`bs-vault-type-option rounded-xl border p-3 text-left transition-colors ${
                            active
                                ? "border-cyan-700 bg-cyan-950/30"
                                : "border-neutral-800 bg-neutral-950 hover:border-neutral-700"
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Icon size={17} className={accent} />
                            <span className="text-sm font-semibold text-white">{label}</span>
                        </div>
                        <p className="mt-1 text-xs text-neutral-500">{description}</p>
                    </button>
                );
            })}
        </div>
    );
}

export function NoteCard({
    note,
    activeFilter,
    viewMode,
    panelOpen,
    openSlot,
    selectable,
    selected,
    selectionMode,
    preferSelectOnClick,
    onOpen,
    onSelect,
    onDragStart,
    onMoveRequest,
    onTogglePin,
    onTrash,
    onRestore,
}: {
    note: Note;
    activeFilter: SidebarFilter;
    viewMode: ViewMode;
    panelOpen: boolean;
    openSlot?: { index: number; color: string; active: boolean };
    selectable?: boolean;
    selected?: boolean;
    selectionMode?: boolean;
    preferSelectOnClick?: boolean;
    onOpen: () => void;
    onSelect?: (note: Note) => void;
    onDragStart?: (note: Note) => void;
    onMoveRequest?: (event: React.MouseEvent, note: Note) => void;
    onTogglePin: (event: React.MouseEvent, noteId: string) => void;
    onTrash: (event: React.MouseEvent, note: Note) => void;
    onRestore: (event: React.MouseEvent, noteId: string) => void;
}) {
    const meta = noteTypeMeta(note.type);
    const TypeIcon = meta.icon;
    const compact = panelOpen || viewMode === "list" || viewMode === "simple";
    const tile = !compact;
    const simple = viewMode === "simple";
    const preview = notePreview(note.content);
    const sharedByMeCount = note.isOwner !== false ? note.sharedWith?.length || 0 : 0;
    const sharedToMe = note.isOwner === false && Boolean(note.owner);
    const canSelect = Boolean(selectable && note.isOwner !== false);
    const noteAccent = note.color || "#22d3ee";
    const noteCardStyle = {
        "--note-accent": noteAccent,
        ...(openSlot ? { borderColor: openSlot.color } : !tile ? { borderColor: selected ? "#67e8f9" : `${noteAccent}33` } : {}),
    } as React.CSSProperties;
    const longPressTimerRef = useRef<number | null>(null);
    const longPressTriggeredRef = useRef(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        if (!menuOpen) return;
        const close = (event: PointerEvent) => {
            if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
        };
        const escape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setMenuOpen(false);
        };
        document.addEventListener("pointerdown", close);
        document.addEventListener("keydown", escape);
        return () => {
            document.removeEventListener("pointerdown", close);
            document.removeEventListener("keydown", escape);
        };
    }, [menuOpen]);

    const clearLongPress = () => {
        if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
    };

    const selectNote = () => {
        if (!canSelect) return;
        onSelect?.(note);
    };

    return (
        <article
            draggable={activeFilter !== "recycle-bin" && note.isOwner !== false}
            onDragStart={() => onDragStart?.(note)}
            onPointerDown={() => {
                if (preferSelectOnClick) return;
                if (!canSelect) return;
                longPressTriggeredRef.current = false;
                clearLongPress();
                longPressTimerRef.current = window.setTimeout(() => {
                    longPressTriggeredRef.current = true;
                    selectNote();
                }, 430);
            }}
            onPointerUp={clearLongPress}
            onPointerLeave={clearLongPress}
            onPointerCancel={clearLongPress}
            onContextMenu={(event) => {
                if (!canSelect) return;
                event.preventDefault();
                if (preferSelectOnClick || longPressTriggeredRef.current) return;
                longPressTriggeredRef.current = true;
                selectNote();
            }}
            onClick={(event) => {
                if (longPressTriggeredRef.current) {
                    event.preventDefault();
                    longPressTriggeredRef.current = false;
                    return;
                }
                if (preferSelectOnClick && canSelect) {
                    event.preventDefault();
                    selectNote();
                    return;
                }
                if (canSelect && (selectionMode || selected || event.shiftKey)) {
                    event.preventDefault();
                    selectNote();
                    return;
                }
                onOpen();
            }}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    if (canSelect && (selectionMode || selected || event.shiftKey)) selectNote();
                    else onOpen();
                }
            }}
            role="button"
            tabIndex={0}
            title={note.title}
            aria-label={`${selected ? "Selected note" : "Open note"} ${note.title}`}
            className={`bs-note-card bs-vault-note-card ${tile ? "bs-vault-tile-bare" : ""} ${openSlot?.active ? "is-open" : ""} group relative cursor-pointer rounded-2xl border shadow-lg transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-500 ${
                tile
                    ? "flex min-h-0 flex-col items-center gap-1.5 border-transparent bg-transparent px-0.5 py-1.5 text-center shadow-none hover:border-transparent hover:bg-transparent"
                    : "flex min-h-0 items-center gap-3 border-neutral-800/80 bg-neutral-950 p-3 hover:border-cyan-800/80 hover:bg-neutral-900/80"
            } ${openSlot?.active ? "bg-neutral-900 ring-1 ring-cyan-500/50" : ""} ${selected ? "border-cyan-400/80 ring-2 ring-cyan-300/70 shadow-[0_0_22px_rgba(34,211,238,0.18)]" : ""}`}
            style={noteCardStyle}
        >
            <div
                ref={menuRef}
                className="hidden"
                onPointerDown={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        setMenuOpen((open) => !open);
                    }}
                    className="grid h-7 w-7 place-items-center rounded-md text-neutral-500 opacity-100 transition hover:bg-neutral-900 hover:text-white sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100"
                    aria-label={`Actions for ${note.title}`}
                    aria-expanded={menuOpen}
                >
                    <MoreHorizontal size={15} />
                </button>
                {menuOpen && (
                    <div className="bs-vault-context-menu absolute right-0 top-8 z-50 w-48 rounded-xl border p-1.5 text-left shadow-[0_20px_55px_rgba(0,0,0,0.72)] backdrop-blur-xl">
                        <button type="button" onClick={(event) => { event.stopPropagation(); setMenuOpen(false); onOpen(); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-neutral-200 hover:bg-white/[0.06]"><Edit3 size={14} /> Open and edit</button>
                        {activeFilter !== "recycle-bin" && note.isOwner !== false && (
                            <button type="button" onClick={(event) => { setMenuOpen(false); onTogglePin(event, note.id); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-neutral-200 hover:bg-white/[0.06]"><Pin size={14} className={note.isPinned ? "fill-amber-300 text-amber-300" : ""} /> {note.isPinned ? "Unpin" : "Pin"}</button>
                        )}
                        {activeFilter !== "recycle-bin" && note.isOwner !== false && (
                            <button type="button" onClick={(event) => { setMenuOpen(false); onMoveRequest?.(event, note); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-neutral-200 hover:bg-white/[0.06]"><FolderIcon size={14} /> Move</button>
                        )}
                        {activeFilter === "recycle-bin" && (
                            <button type="button" onClick={(event) => { setMenuOpen(false); onRestore(event, note.id); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-emerald-200 hover:bg-emerald-950/30"><ArchiveRestore size={14} /> Restore</button>
                        )}
                        {note.isOwner !== false && (
                            <button type="button" onClick={(event) => { setMenuOpen(false); onTrash(event, note); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-rose-200 hover:bg-rose-950/30"><Trash2 size={14} /> {activeFilter === "recycle-bin" ? "Delete permanently" : "Move to Recycle Bin"}</button>
                        )}
                    </div>
                )}
            </div>
            <div className={tile ? "flex min-w-0 flex-col items-center gap-2" : "contents"}>
                <div className={`${tile ? "flex min-w-0 flex-col items-center gap-2 text-center" : "flex min-w-0 items-center gap-2"}`}>
                    {selected && <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-cyan-300 shadow-[0_0_14px_rgba(34,211,238,0.8)]" aria-hidden="true" />}
                    <span className="relative shrink-0">
                        <span className={`bs-vault-note-glyph flex shrink-0 items-center justify-center border border-neutral-800 bg-neutral-900/80 ${
                            tile ? "h-9 w-9 rounded-xl" : "h-8 w-8 rounded-md"
                        }`} style={{ borderColor: `${noteAccent}55`, boxShadow: `0 0 16px ${noteAccent}18` }}>
                            <TypeIcon size={tile ? 18 : 16} style={{ color: noteAccent }} />
                        </span>
                        {tile && note.folder && (
                            <span
                                className="absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-md border border-black/70 bg-black/90 shadow-[0_0_12px_rgba(0,0,0,0.55)]"
                                title={`Inside ${note.folder.name}`}
                                aria-label={`Inside ${note.folder.name}`}
                            >
                                <FolderIcon size={10} style={{ color: note.folder.color }} />
                            </span>
                        )}
                    </span>
                    <div className={`${tile ? "min-w-0 max-w-full" : "min-w-0"}`}>
                        <div className={`${tile ? "flex min-w-0 justify-center gap-1.5" : "flex min-w-0 items-center gap-2"}`}>
                            {openSlot && (
                                <span
                                    className="bs-vault-open-check flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-cyan-700/70 bg-cyan-950/60 text-cyan-200"
                                    style={{ boxShadow: `0 0 12px ${openSlot.color}33` }}
                                    title={openSlot.active ? "Active open note" : "Open note"}
                                >
                                    <Check size={12} />
                                </span>
                            )}
                            {tile && note.isPinned && <Pin size={12} className="shrink-0 fill-amber-300 text-amber-300" />}
                            <h3 title={note.title} className={`bs-vault-note-title block w-16 max-w-full truncate font-medium text-neutral-100 ${
                                tile ? "text-[10px] sm:text-[11px]" : "text-sm"
                            }`}>{note.title}</h3>
                            {sharedByMeCount > 0 && !tile && (
                                <span
                                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-violet-800/70 bg-violet-950/40 text-violet-200"
                                    title="Shared"
                                    aria-label="Shared"
                                >
                                    <Share2 size={12} />
                                </span>
                            )}
                            {sharedToMe && (
                                <span
                                    className={`inline-flex shrink-0 items-center rounded-md border border-neutral-800 bg-neutral-900 text-[11px] font-semibold text-neutral-300 ${
                                        tile ? "h-5 w-5 justify-center" : "gap-1 px-2 py-0.5"
                                    }`}
                                    title={`Shared by @${note.owner?.username}`}
                                >
                                    {note.sharePermission === "edit" ? <Edit3 size={11} className="text-cyan-300" /> : <Eye size={11} className="text-emerald-300" />}
                                    {!tile && (note.sharePermission === "edit" ? "Can edit" : "Can view")}
                                </span>
                            )}
                        </div>
                        {!simple && !tile && <p className="bs-vault-note-kind text-xs text-neutral-500">{meta.label}{note.owner && !note.isOwner ? ` by @${note.owner.username}` : ""}</p>}
                    </div>
                </div>

            </div>

            {!simple && !tile && (
                <p className={`bs-vault-note-preview ${compact ? "hidden xl:block xl:max-w-sm" : "line-clamp-4 flex-1"} whitespace-pre-wrap text-sm leading-6 text-neutral-400`}>
                    {preview || "No content yet."}
                </p>
            )}

            {!compact && !tile && note.tags.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                    {note.tags.slice(0, 3).map((tag) => (
                        <span
                            key={tag}
                            className="bs-vault-note-tag rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1 text-xs text-neutral-400"
                        >
                            #{tag}
                        </span>
                    ))}
                    {note.tags.length > 3 && (
                        <span className="bs-vault-note-tag rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1 text-xs text-neutral-500">
                            +{note.tags.length - 3}
                        </span>
                    )}
                </div>
            )}

            {!simple && !tile && (
            <div className={`bs-vault-note-meta ${compact ? "ml-auto border-0 pt-0" : "mt-4 border-t border-neutral-800 pt-3"} flex items-center justify-between gap-3 text-xs text-neutral-500`}>
                <span className="flex min-w-0 items-center gap-1 truncate">
                    {sharedToMe && note.owner ? (
                        <>
                            <Share2 size={12} className="text-violet-300" />
                            <span className="truncate">Shared by @{note.owner.username}</span>
                        </>
                    ) : note.folder ? (
                        <>
                            <FolderIcon size={12} style={{ color: note.folder.color }} />
                            <span className="truncate">{note.folder.name}</span>
                        </>
                    ) : (
                        <>
                            <FolderIcon size={12} className="text-neutral-600" />
                            <span>No folder</span>
                        </>
                    )}
                </span>
                <span className="shrink-0">{timeAgo(note.updatedAt)}</span>
            </div>
            )}
        </article>
    );
}

export function NoteSkeleton() {
    return (
        <div className="bs-vault-skeleton flex animate-pulse flex-col items-center gap-2 rounded-2xl border border-transparent px-1.5 py-2">
            <div className="h-9 w-9 rounded-xl border border-neutral-800 bg-neutral-900" />
            <div className="h-3 w-16 rounded bg-neutral-800" />
        </div>
    );
}

export function FolderTile({
    folder,
    selected,
    selectionMode,
    onOpen,
    onSelect,
    onDropNote,
    onDragStartFolder,
    onDragEndFolder,
    onDropFolder,
    onEdit,
    onTogglePin,
    onTrash,
}: {
    folder: FolderType;
    selected?: boolean;
    selectionMode?: boolean;
    onOpen: () => void;
    onSelect?: (folder: FolderType) => void;
    onDropNote?: () => void;
    onDragStartFolder?: (folder: FolderType) => void;
    onDragEndFolder?: () => void;
    onDropFolder?: () => void;
    onEdit?: (folder: FolderType) => void;
    onTogglePin?: (folder: FolderType) => void;
    onTrash?: (folder: FolderType) => void;
}) {
    const longPressTimerRef = useRef<number | null>(null);
    const longPressTriggeredRef = useRef(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        if (!menuOpen) return;
        const close = (event: PointerEvent) => {
            if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
        };
        const escape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setMenuOpen(false);
        };
        document.addEventListener("pointerdown", close);
        document.addEventListener("keydown", escape);
        return () => {
            document.removeEventListener("pointerdown", close);
            document.removeEventListener("keydown", escape);
        };
    }, [menuOpen]);

    const clearLongPress = () => {
        if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
    };

    const selectFolder = () => onSelect?.(folder);

    return (
        <div
            draggable={Boolean(onDragStartFolder)}
            className={`bs-vault-folder-tile bs-vault-tile-bare group relative flex min-w-0 flex-col items-center gap-1 rounded-2xl border border-transparent bg-transparent px-0 py-1 text-center transition-colors hover:border-transparent hover:bg-transparent ${
                selected ? "ring-1 ring-cyan-500/70" : ""
            }`}
            onDragOver={(event) => {
                if (!onDropNote && !onDropFolder) return;
                event.preventDefault();
            }}
            onDrop={(event) => {
                if (!onDropNote && !onDropFolder) return;
                event.preventDefault();
                event.stopPropagation();
                onDropNote?.();
                onDropFolder?.();
            }}
            onDragStart={(event) => {
                if (!onDragStartFolder) return;
                event.dataTransfer.effectAllowed = "move";
                onDragStartFolder(folder);
            }}
            onDragEnd={onDragEndFolder}
        >
            <div
                ref={menuRef}
                className="hidden"
                onPointerDown={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        setMenuOpen((open) => !open);
                    }}
                    className="grid h-7 w-7 place-items-center rounded-md text-neutral-500 opacity-100 transition hover:bg-neutral-900 hover:text-white sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100"
                    aria-label={`Actions for folder ${folder.name}`}
                    aria-expanded={menuOpen}
                >
                    <MoreHorizontal size={15} />
                </button>
                {menuOpen && (
                    <div className="bs-vault-context-menu absolute right-0 top-8 z-50 w-48 rounded-xl border p-1.5 text-left shadow-[0_20px_55px_rgba(0,0,0,0.72)] backdrop-blur-xl">
                        <button type="button" onClick={(event) => { event.stopPropagation(); setMenuOpen(false); onOpen(); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-neutral-200 hover:bg-white/[0.06]"><FolderIcon size={14} /> Open</button>
                        {onTogglePin && <button type="button" onClick={(event) => { event.stopPropagation(); setMenuOpen(false); onTogglePin(folder); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-neutral-200 hover:bg-white/[0.06]"><Pin size={14} className={folder.isPinned ? "fill-amber-300 text-amber-300" : ""} /> {folder.isPinned ? "Unpin" : "Pin"}</button>}
                        {onEdit && <button type="button" onClick={(event) => { event.stopPropagation(); setMenuOpen(false); onEdit(folder); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-neutral-200 hover:bg-white/[0.06]"><Edit3 size={14} /> Edit</button>}
                        {onTrash && <button type="button" onClick={(event) => { event.stopPropagation(); setMenuOpen(false); onTrash(folder); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-rose-200 hover:bg-rose-950/30"><Trash2 size={14} /> Recycle Bin</button>}
                    </div>
                )}
            </div>
            {selected && <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-cyan-300 shadow-[0_0_14px_rgba(34,211,238,0.8)]" aria-hidden="true" />}
            <button
                type="button"
                onPointerDown={() => {
                    longPressTriggeredRef.current = false;
                    clearLongPress();
                    longPressTimerRef.current = window.setTimeout(() => {
                        longPressTriggeredRef.current = true;
                        selectFolder();
                    }, 430);
                }}
                onPointerUp={clearLongPress}
                onPointerLeave={clearLongPress}
                onPointerCancel={clearLongPress}
                onContextMenu={(event) => {
                    event.preventDefault();
                    selectFolder();
                }}
                onClick={(event) => {
                    if (longPressTriggeredRef.current) {
                        event.preventDefault();
                        longPressTriggeredRef.current = false;
                        return;
                    }
                    if (selectionMode || event.shiftKey) {
                        event.preventDefault();
                        selectFolder();
                        return;
                    }
                    onOpen();
                }}
                title={folder.name}
                aria-label={`${selected ? "Selected folder" : "Open folder"} ${folder.name}`}
                className="flex min-w-0 flex-col items-center gap-1 text-center"
            >
                <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-neutral-800 bg-neutral-900/80 shadow-[0_0_12px_rgba(8,145,178,0.05)] transition-transform group-hover:-translate-y-0.5"
                    style={{ boxShadow: `0 0 11px ${folder.color || "#22d3ee"}22` }}
                >
                    <FolderIcon size={18} style={{ color: folder.color }} />
                </span>
                <span className="min-w-0 max-w-full">
                    <span className="flex min-w-0 justify-center gap-1.5">
                        {folder.isPinned && <Pin size={12} className="shrink-0 fill-amber-300 text-amber-300" />}
                        <span title={folder.name} className="block w-16 max-w-full truncate text-[10px] font-medium text-white sm:text-[11px]">{folder.name}</span>
                    </span>
                </span>
            </button>
        </div>
    );
}

export function OpenNotePane({
    note,
    slot,
    active,
    folders,
    onActivate,
    onClose,
    onUpdated,
    onRemove,
    onError,
    onNotice,
    onDirtyChange,
    onConfirmDiscard,
}: {
    note: Note;
    slot: { index: number; color: string; active: boolean };
    active: boolean;
    folders: FolderType[];
    onActivate: () => void;
    onClose: () => void;
    onUpdated: (note: Note) => void;
    onRemove: (noteId: string) => void;
    onError: (message: string) => void;
    onNotice: (message: string) => void;
    onDirtyChange?: (dirty: boolean) => void;
    onConfirmDiscard?: (discard: () => void) => void;
}) {
    const restoredDraft = readOpenNoteDraft(note.id, note.updatedAt);
    const meta = noteTypeMeta(note.type);
    const TypeIcon = meta.icon;
    const [editing, setEditing] = useState(Boolean(restoredDraft));
    const [title, setTitle] = useState(restoredDraft?.title ?? note.title);
    const [content, setContent] = useState(restoredDraft?.content ?? note.content);
    const [tags, setTags] = useState(restoredDraft?.tags ?? note.tags.join(", "));
    const [folder, setFolder] = useState<string | null>(restoredDraft?.folder ?? note.folder?.id ?? null);
    const [type, setType] = useState<NoteType>(restoredDraft?.type ?? note.type);
    const [color, setColor] = useState(normalizeVaultColor(restoredDraft?.color ?? note.color));
    const [saving, setSaving] = useState(false);
    const [savedText, setSavedText] = useState("");
    const [isOnline, setIsOnline] = useState(() => typeof navigator === "undefined" ? true : navigator.onLine);
    const [shareUsername, setShareUsername] = useState("");
    const [sharePermission, setSharePermission] = useState<"view" | "edit">("view");
    const [shareSaving, setShareSaving] = useState(false);
    const [shareMatches, setShareMatches] = useState<ProfileSearchUser[]>([]);
    const [shareSearching, setShareSearching] = useState(false);
    const [shareSearchOpen, setShareSearchOpen] = useState(false);
    const [shareUpdatingUser, setShareUpdatingUser] = useState<string | null>(null);
    const [spaceTargets, setSpaceTargets] = useState<SpaceType[]>([]);
    const [spaceTargetsLoading, setSpaceTargetsLoading] = useState(false);
    const [materialSpaceId, setMaterialSpaceId] = useState("");
    const [materialKind, setMaterialKind] = useState<SpaceMaterial["kind"]>("material");
    const [materialSyncMode, setMaterialSyncMode] = useState<"frozen" | "live">("frozen");
    const [materialPermissions, setMaterialPermissions] = useState({ allowComments: true, allowCopy: true, allowRemix: true });
    const [materialSharing, setMaterialSharing] = useState(false);
    const [toolsOpen, setToolsOpen] = useState(false);
    const [activeUtility, setActiveUtility] = useState<"history" | "access" | "share" | null>(null);
    const [versions, setVersions] = useState<NoteVersion[]>([]);
    const [versionsLoading, setVersionsLoading] = useState(false);
    const [versionRestoring, setVersionRestoring] = useState<string | null>(null);
    const autosaveSequenceRef = useRef(0);
    const acknowledgedUpdatedAtRef = useRef<string | null>(null);
    const activeNoteIdRef = useRef(note.id);
    const editorStateRef = useRef({ title, content, tags, folder, type, color });
    const initialTags = note.tags.join(", ");
    const dirty = editing && (
        title !== note.title ||
        content !== note.content ||
        tags !== initialTags ||
        folder !== (note.folder?.id || null) ||
        type !== note.type ||
        color !== normalizeVaultColor(note.color)
    );
    const editFolderSuggestions = useMemo(() => {
        const haystack = `${title} ${tags} ${notePreview(content)}`.toLowerCase();
        if (!haystack.trim()) return [];
        return folders
            .map((item) => {
                const score = item.name.toLowerCase().split(/\s+/).filter(Boolean)
                    .reduce((total, word) => total + (haystack.includes(word) ? 2 : 0), 0)
                    + (item.noteCount > 0 ? 0.2 : 0)
                    + (item.isPinned ? 0.5 : 0);
                return { folder: item, score };
            })
            .filter((item) => item.score > 0 && item.folder.id !== folder)
            .sort((a, b) => b.score - a.score || a.folder.name.localeCompare(b.folder.name))
            .slice(0, 3)
            .map((item) => item.folder);
    }, [content, folder, folders, tags, title]);

    useEffect(() => {
        editorStateRef.current = { title, content, tags, folder, type, color };
    }, [color, content, folder, tags, title, type]);

    useEffect(() => () => {
        autosaveSequenceRef.current += 1;
    }, []);

    useEffect(() => {
        const query = normalizeUsernameSearch(shareUsername);
        if (query.length < 2) {
            setShareMatches([]);
            setShareSearching(false);
            return;
        }

        let alive = true;
        setShareSearching(true);
        const timer = window.setTimeout(() => {
            searchProfiles(query)
                .then((users) => {
                    if (alive) setShareMatches(users);
                })
                .catch(() => {
                    if (alive) setShareMatches([]);
                })
                .finally(() => {
                    if (alive) setShareSearching(false);
                });
        }, 220);

        return () => {
            alive = false;
            window.clearTimeout(timer);
        };
    }, [shareUsername]);

    useEffect(() => {
        if (activeNoteIdRef.current === note.id && acknowledgedUpdatedAtRef.current === note.updatedAt) {
            acknowledgedUpdatedAtRef.current = null;
            return;
        }
        if (activeNoteIdRef.current === note.id && editing) {
            return;
        }
        activeNoteIdRef.current = note.id;
        const draft = readOpenNoteDraft(note.id, note.updatedAt);
        setTitle(draft?.title ?? note.title);
        setContent(draft?.content ?? note.content);
        setTags(draft?.tags ?? note.tags.join(", "));
        setFolder(draft?.folder ?? note.folder?.id ?? null);
        setType(draft?.type ?? note.type);
        setColor(normalizeVaultColor(draft?.color ?? note.color));
        setEditing(Boolean(draft));
        if (draft) setSavedText("Draft restored");
    }, [editing, note]);

    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    useEffect(() => {
        const updateOnlineState = () => setIsOnline(typeof navigator === "undefined" ? true : navigator.onLine);
        window.addEventListener("online", updateOnlineState);
        window.addEventListener("offline", updateOnlineState);
        return () => {
            window.removeEventListener("online", updateOnlineState);
            window.removeEventListener("offline", updateOnlineState);
        };
    }, []);

    useEffect(() => {
        if (!dirty) return;
        const timer = window.setTimeout(() => {
            writeOpenNoteDraft(note.id, {
                title,
                content,
                tags,
                folder,
                type,
                color,
                baseUpdatedAt: note.updatedAt,
            });
            setSavedText("Draft saved locally");
        }, 350);
        return () => window.clearTimeout(timer);
    }, [color, content, dirty, folder, note.id, note.updatedAt, tags, title, type]);

    useEffect(() => {
        if (!dirty || !title.trim() || note.canEdit === false) return;
        if (!isOnline) {
            setSavedText("Offline draft saved");
            return;
        }
        const timer = window.setTimeout(() => {
            const requestSequence = ++autosaveSequenceRef.current;
            const snapshot = { title, content, tags, folder, type, color: normalizeVaultColor(color) };
            setSavedText("Saving...");
            autosaveNote(note.id, {
                title: snapshot.title.trim(),
                content: snapshot.content.trim(),
                type: snapshot.type,
                color: snapshot.color,
                tags: snapshot.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
                ...(note.isOwner !== false ? { folder: snapshot.folder } : {}),
            })
                .then((updated) => {
                    if (requestSequence !== autosaveSequenceRef.current) return;
                    acknowledgedUpdatedAtRef.current = updated.updatedAt;
                    onUpdated(updated);
                    const current = editorStateRef.current;
                    const unchanged = current.title === snapshot.title
                        && current.content === snapshot.content
                        && current.tags === snapshot.tags
                        && current.folder === snapshot.folder
                        && current.type === snapshot.type
                        && current.color === snapshot.color;
                    if (unchanged) {
                        clearOpenNoteDraft(note.id);
                        setSavedText("Saved");
                    } else {
                        writeOpenNoteDraft(note.id, {
                            ...current,
                            baseUpdatedAt: updated.updatedAt,
                        });
                        setSavedText("Saving newer edits...");
                    }
                })
                .catch(() => {
                    if (requestSequence === autosaveSequenceRef.current) setSavedText("Local draft saved");
                });
        }, 2200);
        return () => window.clearTimeout(timer);
    }, [color, content, dirty, folder, isOnline, note.canEdit, note.id, note.isOwner, onUpdated, tags, title, type]);

    const resetDraft = () => {
        autosaveSequenceRef.current += 1;
        setTitle(note.title);
        setContent(note.content);
        setTags(note.tags.join(", "));
        setFolder(note.folder?.id || null);
        setType(note.type);
        setColor(normalizeVaultColor(note.color));
        setEditing(false);
        clearOpenNoteDraft(note.id);
        onDirtyChange?.(false);
    };

    useEffect(() => {
        if (note.isOwner === false) return;
        let alive = true;
        setSpaceTargetsLoading(true);
        fetchJoinedSpaces({ limit: 100 })
            .then((data) => {
                if (!alive) return;
                const available = (data.spaces || []).filter((space) => space.canView !== false && !space.privateStats);
                setSpaceTargets(available);
                setMaterialSpaceId((current) => current || available[0]?.id || "");
            })
            .catch(() => {
                if (alive) setSpaceTargets([]);
            })
            .finally(() => {
                if (alive) setSpaceTargetsLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [note.id, note.isOwner]);

    const save = async () => {
        if (!title.trim() || saving) return;
        autosaveSequenceRef.current += 1;
        setSaving(true);
        try {
            const payload: Partial<CreateNoteInput> = {
                title: title.trim(),
                content: content.trim(),
                type,
                color: normalizeVaultColor(color),
                tags: tags.split(",").map((tag) => tag.trim()).filter(Boolean),
            };
            if (note.isOwner !== false) payload.folder = folder;
            const updated = await updateNote(note.id, payload);
            clearOpenNoteDraft(note.id);
            onUpdated(updated);
            setSavedText("Saved just now");
            setEditing(false);
            onDirtyChange?.(false);
        } catch (err: unknown) {
            onError(err instanceof Error ? err.message : "Failed to save note");
        } finally {
            setSaving(false);
        }
    };

    const favourite = async () => {
        try {
            const isPinned = await togglePin(note.id);
            onUpdated({ ...note, isPinned });
        } catch (err: unknown) {
            onError(err instanceof Error ? err.message : "Failed to update favourite");
        }
    };

    const recycle = async () => {
        try {
            await toggleTrash(note.id);
            onRemove(note.id);
        } catch (err: unknown) {
            onError(err instanceof Error ? err.message : "Failed to move note");
        }
    };

    const share = async () => {
        const username = normalizeUsernameSearch(shareUsername);
        if (!username || shareSaving) return;
        setShareSaving(true);
        try {
            const updated = await shareNote(note.id, username, sharePermission);
            onUpdated(updated);
            onNotice(`Access granted: @${username} can ${sharePermission === "edit" ? "edit" : "view"} "${note.title}".`);
            setShareUsername("");
            setShareMatches([]);
            setShareSearchOpen(false);
        } catch (err: unknown) {
            onError(err instanceof Error ? `Could not share with @${username}: ${err.message}` : `Could not share with @${username}.`);
        } finally {
            setShareSaving(false);
        }
    };

    const removeAccess = async (userId: string) => {
        setShareSaving(true);
        try {
            const updated = await unshareNote(note.id, userId);
            onUpdated(updated);
            onNotice("Access removed successfully.");
        } catch (err: unknown) {
            onError(err instanceof Error ? `Could not remove access: ${err.message}` : "Could not remove access.");
        } finally {
            setShareSaving(false);
        }
    };

    const updateAccess = async (username: string, userId: string, permission: "view" | "edit") => {
        setShareUpdatingUser(userId);
        try {
            const updated = await shareNote(note.id, username, permission);
            onUpdated(updated);
            onNotice(`Access updated: @${username} can ${permission === "edit" ? "edit" : "view"} "${note.title}".`);
        } catch (err: unknown) {
            onError(err instanceof Error ? `Could not update @${username}: ${err.message}` : `Could not update @${username}.`);
        } finally {
            setShareUpdatingUser(null);
        }
    };

    const shareMaterial = async () => {
        if (!materialSpaceId || materialSharing) return;
        setMaterialSharing(true);
        try {
            const material = await shareNoteToSpace(materialSpaceId, {
                noteId: note.id,
                kind: materialKind,
                syncMode: materialSyncMode,
                permissions: materialPermissions,
            });
            const target = spaceTargets.find((space) => space.id === material.spaceId);
            onNotice(`Space share complete: "${material.title}" is now in ${target?.title || "the selected space"}.`);
        } catch (err: unknown) {
            onError(err instanceof Error ? `Could not share to space: ${err.message}` : "Could not share to space.");
        } finally {
            setMaterialSharing(false);
        }
    };

    const loadVersions = async () => {
        setVersionsLoading(true);
        try {
            setVersions(await fetchNoteVersions(note.id));
        } catch (err: unknown) {
            onError(err instanceof Error ? err.message : "Failed to load history");
        } finally {
            setVersionsLoading(false);
        }
    };

    const restoreVersion = async (versionId: string) => {
        if (versionRestoring) return;
        setVersionRestoring(versionId);
        try {
            const restored = await restoreNoteVersion(note.id, versionId);
            clearOpenNoteDraft(note.id);
            onUpdated(restored);
            setVersions([]);
            onNotice("Version restored");
        } catch (err: unknown) {
            onError(err instanceof Error ? err.message : "Failed to restore version");
        } finally {
            setVersionRestoring(null);
        }
    };

    const shareQuery = normalizeUsernameSearch(shareUsername);
    const exactShareMatch = shareMatches.some((user) => user.username.toLowerCase() === shareQuery.toLowerCase());
    const showShareMatches = shareSearchOpen && !exactShareMatch && (
        shareSearching || shareMatches.length > 0 || shareQuery.length >= 2
    );
    const noteAccent = editing ? normalizeVaultColor(color) : normalizeVaultColor(note.color);

    return (
        <article
            className={`bs-vault-open-note relative min-w-0 overflow-hidden rounded-2xl border shadow-[0_0_70px_rgba(8,145,178,0.12)] transition-colors ${
                active ? "border-cyan-700" : "border-neutral-800"
            }`}
            style={{
                "--note-accent": noteAccent,
                borderColor: active ? `${noteAccent}88` : `${noteAccent}33`,
                boxShadow: active ? `0 0 0 1px ${noteAccent}44, 0 0 70px ${noteAccent}18` : `0 0 44px ${noteAccent}0d`,
            } as React.CSSProperties}
        >
            <div
                className="bs-vault-open-note-header flex cursor-pointer items-start justify-between gap-3 border-b p-4"
                style={{
                    borderColor: `${noteAccent}24`,
                    background: `linear-gradient(135deg, ${noteAccent}18, rgba(0,0,0,0.38) 62%), radial-gradient(circle at 100% 0%, ${noteAccent}12, transparent 44%)`,
                }}
                onClick={onActivate}
            >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="min-w-0 shrink-0">
                        <span
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border"
                            style={{
                                borderColor: `${noteAccent}aa`,
                                color: noteAccent,
                                background: `radial-gradient(circle at 35% 20%, ${noteAccent}38, rgba(0,0,0,0.68) 64%)`,
                                boxShadow: `0 0 0 1px ${noteAccent}22, 0 0 20px ${noteAccent}3f`,
                            }}
                        >
                            <TypeIcon size={14} />
                        </span>
                    </div>
                    <div className="min-w-0">
                        <h2 className="truncate text-lg font-black tracking-tight text-white">{note.title}</h2>
                        {note.isOwner === false && note.owner && (
                            <p className="truncate text-[11px] font-semibold text-neutral-500">Shared by @{note.owner.username}</p>
                        )}
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    {!editing && note.canEdit !== false && (
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                setEditing(true);
                            }}
                            className="rounded-md p-2 text-neutral-400 transition-colors hover:bg-neutral-900 hover:text-cyan-300"
                            title="Edit"
                        >
                            <Edit3 size={16} />
                        </button>
                    )}
                    {note.isOwner !== false && (
                        <>
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    favourite();
                                }}
                                className={`rounded-md p-2 transition-colors hover:bg-neutral-900 ${
                                    note.isPinned ? "text-amber-300" : "text-neutral-400 hover:text-amber-300"
                                }`}
                                title={note.isPinned ? "Remove favourite" : "Favourite"}
                            >
                                <Pin size={16} className={note.isPinned ? "fill-current" : ""} />
                            </button>
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    recycle();
                                }}
                                className="rounded-md p-2 text-neutral-400 transition-colors hover:bg-neutral-900 hover:text-rose-300"
                                title="Move to Recycle Bin"
                            >
                                <Trash2 size={16} />
                            </button>
                        </>
                    )}
                    {note.isOwner !== false && !editing && (
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                setToolsOpen((open) => !open);
                                if (toolsOpen) setActiveUtility(null);
                            }}
                            className={`rounded-md p-2 transition-colors hover:bg-neutral-900 ${
                                toolsOpen ? "text-cyan-200" : "text-neutral-400 hover:text-cyan-300"
                            }`}
                            title="More tools"
                            aria-label="More tools"
                            aria-expanded={toolsOpen}
                        >
                            <MoreHorizontal size={17} />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            onClose();
                        }}
                        className="rounded-md p-2 text-neutral-500 transition-colors hover:bg-neutral-900 hover:text-white"
                        title="Close note"
                    >
                        <X size={17} />
                    </button>
                </div>
            </div>

            <div className="space-y-4 p-4">
                {editing ? (
                    <>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={title}
                                onChange={(event) => setTitle(event.target.value)}
                                className="bs-vault-title-input bs-vault-single-surface-input min-w-0 flex-1 rounded-lg border px-3 py-2.5 text-sm font-semibold text-white outline-none placeholder:text-neutral-600"
                                placeholder="Note title..."
                            />
                            <label
                                className="grid h-10 w-10 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-md border border-neutral-800 shadow-[0_0_18px_rgba(34,211,238,0.08)] transition hover:border-cyan-800"
                                style={{ backgroundColor: color }}
                                title="Pick note colour"
                            >
                                <span className="sr-only">Pick note colour</span>
                                <input
                                    type="color"
                                    value={color}
                                    onChange={(event) => setColor(normalizeVaultColor(event.target.value))}
                                    className="h-12 w-12 cursor-pointer opacity-0"
                                    aria-label="Pick note colour"
                                />
                            </label>
                        </div>
                        <RichEditor
                            value={content}
                            onChange={setContent}
                            rows={10}
                            historyKey={`open-${note.id}`}
                            placeholder="Write inside the Vault..."
                        />
                        <details className="bs-vault-details rounded-xl border">
                            <summary className="bs-vault-details-summary flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-bold text-neutral-300">
                                Details
                                <ChevronRight size={15} className="text-neutral-500" />
                            </summary>
                            <div className="bs-vault-details-body space-y-4 border-t p-4">
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <label className="block">
                                        <span className="mb-2 flex items-center gap-2 text-xs font-semibold text-neutral-500">
                                            <Tags size={13} />
                                            Tags
                                        </span>
                                        <input
                                            type="text"
                                            value={tags}
                                            onChange={(event) => setTags(event.target.value)}
                                            placeholder="ideas, code, plans"
                                            className="bs-vault-soft-input bs-vault-single-surface-input w-full rounded-lg border px-3 py-2.5 text-sm text-white outline-none placeholder:text-neutral-600"
                                        />
                                    </label>
                                    {note.isOwner !== false && (
                                <label className="block">
                                    <span className="mb-2 flex items-center gap-2 text-xs font-semibold text-neutral-500">
                                        <FolderIcon size={13} />
                                        Folder
                                    </span>
                                    <select
                                        value={folder || ""}
                                        onChange={(event) => setFolder(event.target.value || null)}
                                        className="bs-vault-soft-input bs-vault-single-surface-input w-full rounded-lg border px-3 py-2.5 text-sm text-white outline-none"
                                    >
                                        <option value="">No folder</option>
                                        {folders.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {item.name}
                                            </option>
                                        ))}
                                    </select>
                                    {editFolderSuggestions.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {editFolderSuggestions.map((item) => (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    onClick={() => setFolder(item.id)}
                                                    className="inline-flex items-center gap-2 rounded-md border border-neutral-800 bg-black/35 px-2.5 py-1.5 text-[11px] font-bold text-neutral-400 hover:border-cyan-800 hover:text-cyan-100"
                                                >
                                                    <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: item.color }} />
                                                    {item.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </label>
                                    )}
                                </div>
                            </div>
                        </details>
                        <div
                            className="bs-vault-save-strip sticky bottom-3 z-10 flex flex-col gap-3 rounded-lg border bg-black/85 p-3 shadow-[0_-16px_36px_rgba(0,0,0,0.35)] backdrop-blur sm:flex-row sm:items-center"
                            style={{ borderColor: `${color}55`, boxShadow: `0 -16px 36px rgba(0,0,0,0.35), 0 0 26px ${color}12` }}
                        >
                            <p className="mr-auto text-xs text-neutral-500">
                                {title.trim() ? (savedText || "Edits stay local until saved.") : "A title is required."}
                            </p>
                            <button
                                type="button"
                                onClick={() => {
                                    if (dirty && onConfirmDiscard) onConfirmDiscard(resetDraft);
                                    else resetDraft();
                                }}
                                className="rounded-md border border-neutral-800 px-4 py-2 text-sm font-semibold text-neutral-300 transition-colors hover:bg-neutral-900"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={!title.trim() || saving}
                                className="bs-vault-modal-action inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
                                style={{ "--vault-action-color": color } as React.CSSProperties}
                            >
                                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                Save note
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {(savedText || (note.owner && !note.isOwner)) && (
                        <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                            {savedText && <span>{savedText}</span>}
                            {note.owner && !note.isOwner && (
                                <span className="inline-flex items-center gap-1 rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1 text-neutral-300">
                                    {note.sharePermission === "edit" ? <Edit3 size={12} className="text-cyan-300" /> : <Eye size={12} className="text-emerald-300" />}
                                    Shared by @{note.owner.username} · {note.sharePermission === "edit" ? "You can edit" : "You can view"}
                                </span>
                            )}
                        </div>
                        )}
                        <div
                            className="bs-vault-document-surface bs-vault-note-canvas min-h-[260px] rounded-xl border p-5 text-sm leading-7 text-neutral-300"
                            style={{
                                borderColor: `${noteAccent}30`,
                            }}
                        >
                            {note.content ? (
                                <div className="vault-rich-view break-words" dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(note.content) }} />
                            ) : (
                                <p className="text-neutral-500">No content yet.</p>
                            )}
                        </div>
                        <div className="-mt-2 flex justify-end pr-1 text-xs font-semibold text-neutral-600">
                            {timeAgo(note.updatedAt)}
                        </div>

                        <details className="bs-vault-details rounded-xl border border-neutral-900 bg-black/20">
                            <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wide text-neutral-600">
                                Details
                                <ChevronRight size={14} />
                            </summary>
                            <div className="space-y-3 border-t border-neutral-900 px-3 py-3 text-xs text-neutral-500">
                                <div className="flex flex-wrap gap-2">
                                    <span className="inline-flex items-center gap-1 rounded-md border border-neutral-800 bg-black/35 px-2 py-1">
                                        <TypeIcon size={11} style={{ color: noteAccent }} />
                                        {meta.label}
                                    </span>
                                    <span className="inline-flex items-center gap-1 rounded-md border border-neutral-800 bg-black/35 px-2 py-1">
                                        <FolderIcon size={11} />
                                        {note.folder?.name || "No folder"}
                                    </span>
                                    {note.wordCount > 0 && (
                                        <span className="inline-flex items-center gap-1 rounded-md border border-neutral-800 bg-black/35 px-2 py-1">
                                            <ScrollText size={11} />
                                            {note.wordCount} words
                                        </span>
                                    )}
                                </div>
                                {note.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {note.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className="inline-flex items-center gap-1 rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1 text-xs text-neutral-400"
                                            >
                                                <Hash size={11} />
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </details>

                        {note.isOwner !== false && toolsOpen && (
                            <div className="bs-vault-utility-switcher flex justify-end gap-3 border-t border-cyan-950/40 pt-3">
                                {[
                                    { id: "history" as const, label: "History", icon: ArchiveRestore, count: note.versionCount || versions.length || 0, onOpen: loadVersions },
                                    { id: "access" as const, label: "Access", icon: ShieldCheck, count: note.sharedWith?.length || 0 },
                                    { id: "share" as const, label: "Share to Space", icon: Send },
                                ].map(({ id, label, icon: Icon, count, onOpen }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => {
                                            setActiveUtility((current) => current === id ? null : id);
                                            if (id === "history" && versions.length === 0 && !versionsLoading) onOpen?.();
                                        }}
                                        className={`bs-vault-utility-button group relative grid h-10 w-10 place-items-center rounded-xl border transition-colors ${
                                            activeUtility === id
                                                ? "border-cyan-700 bg-cyan-950/35 text-cyan-100"
                                                : "border-neutral-800 bg-black/35 text-neutral-400 hover:border-cyan-800 hover:text-cyan-200"
                                        }`}
                                        title={label}
                                        aria-label={label}
                                        aria-pressed={activeUtility === id}
                                    >
                                        <Icon size={17} />
                                        {typeof count === "number" && count > 0 && (
                                            <span className="absolute -right-1 -top-1 min-w-4 rounded-full border border-cyan-900 bg-cyan-950 px-1 text-[10px] font-black text-cyan-100">
                                                {count}
                                            </span>
                                        )}
                                        <span className="pointer-events-none absolute bottom-[calc(100%+0.45rem)] left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1 text-[11px] font-bold text-neutral-200 shadow-xl group-hover:block group-focus-visible:block">
                                            {label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {note.isOwner !== false && toolsOpen && activeUtility === "history" && (
                            <div className="space-y-2 rounded-xl border border-cyan-900/40 bg-black/35 p-4">
                                    {versionsLoading ? (
                                        <div className="flex items-center gap-2 text-xs text-neutral-500">
                                            <Loader2 size={13} className="animate-spin" />
                                            Loading history
                                        </div>
                                    ) : versions.length > 0 ? versions.map((version) => (
                                        <div key={version.id} className="flex items-center gap-3 rounded-md border border-neutral-800 bg-black/45 px-3 py-2 text-sm">
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate font-semibold text-neutral-200">{version.title || "Untitled version"}</p>
                                                <p className="text-xs text-neutral-500">{timeAgo(version.savedAt)} - {version.reason}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => restoreVersion(version.id)}
                                                disabled={Boolean(versionRestoring)}
                                                className="rounded-md border border-cyan-900/60 px-3 py-1.5 text-xs font-bold text-cyan-100 hover:bg-cyan-950/30 disabled:opacity-50"
                                            >
                                                {versionRestoring === version.id ? "Restoring" : "Restore"}
                                            </button>
                                        </div>
                                    )) : (
                                        <p className="text-sm text-neutral-500">Saved versions will appear after your next manual save.</p>
                                    )}
                            </div>
                        )}

                        {note.isOwner !== false && toolsOpen && activeUtility === "access" && (
                            <div className="bs-vault-access-panel hidden-scrollbar max-h-[min(54vh,520px)] space-y-3 overflow-y-auto rounded-xl border border-cyan-900/40 bg-black/35 p-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                <div className="flex flex-col gap-2 sm:flex-row">
                                    <div className="relative min-w-0 flex-1">
                                        <input
                                            type="text"
                                            value={shareUsername}
                                            onChange={(event) => {
                                                setShareUsername(event.target.value);
                                                setShareSearchOpen(true);
                                            }}
                                            onFocus={() => setShareSearchOpen(true)}
                                            onBlur={() => window.setTimeout(() => setShareSearchOpen(false), 140)}
                                            placeholder="Search username..."
                                            className="bs-vault-access-search w-full rounded-md border border-neutral-800 bg-black/65 px-3 py-2 text-sm text-white outline-none placeholder:text-neutral-600 focus:border-cyan-700"
                                        />
                                        {showShareMatches && (
                                            <div className="hidden-scrollbar absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-64 overflow-y-auto rounded-lg border border-cyan-900/60 bg-neutral-950 shadow-2xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                                {shareSearching ? (
                                                    <div className="flex items-center gap-2 px-3 py-2 text-xs text-neutral-500">
                                                        <Loader2 size={13} className="animate-spin" />
                                                        Searching users
                                                    </div>
                                                ) : shareMatches.length > 0 ? (
                                                    shareMatches.map((user) => (
                                                        <button
                                                            key={user.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setShareUsername(user.username);
                                                                setShareMatches([]);
                                                                setShareSearchOpen(false);
                                                            }}
                                                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-neutral-300 transition-colors hover:bg-neutral-900"
                                                        >
                                                            {user.avatarUrl ? (
                                                                <img src={user.avatarUrl} alt="" className="h-7 w-7 rounded-md object-cover" />
                                                            ) : (
                                                                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-neutral-800 text-xs font-bold text-cyan-300">
                                                                    {(user.displayName || user.username).slice(0, 1).toUpperCase()}
                                                                </span>
                                                            )}
                                                            <span className="min-w-0 flex-1">
                                                                <span className="block truncate font-semibold text-white">
                                                                    {user.displayName || user.username}
                                                                </span>
                                                                <span className="block truncate text-xs text-neutral-500">@{user.username}</span>
                                                            </span>
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className="px-3 py-2 text-xs text-neutral-500">
                                                        No matching username found.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <select
                                        value={sharePermission}
                                        onChange={(event) => setSharePermission(event.target.value as "view" | "edit")}
                                        className="bs-vault-access-permission rounded-md border border-neutral-800 bg-black/65 px-3 py-2 text-sm text-white outline-none focus:border-cyan-700"
                                    >
                                        <option value="view">Can view</option>
                                        <option value="edit">Can edit</option>
                                    </select>
                                    <button
                                        type="button"
                                        onClick={share}
                                        disabled={!normalizeUsernameSearch(shareUsername) || shareSaving}
                                        className="bs-vault-access-submit inline-flex items-center justify-center gap-2 rounded-md border border-cyan-700 bg-cyan-700 px-3 py-2 text-sm font-bold text-white shadow-[0_0_24px_rgba(8,145,178,0.14)] hover:bg-cyan-600 disabled:opacity-50"
                                    >
                                        {shareSaving ? <Loader2 size={15} className="animate-spin" /> : <UsersRound size={15} />}
                                        Share
                                    </button>
                                </div>
                                {note.sharedWith && note.sharedWith.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {note.sharedWith.map((user) => (
                                            <div key={user.id} className="bs-vault-access-row flex items-center gap-2 rounded-md border border-neutral-800 bg-black/45 px-3 py-2 text-sm">
                                                <span className="hidden" aria-hidden="true">
                                                    @{user.username} · {user.permission}
                                                </span>
                                                <span className="min-w-0 flex-1 truncate text-neutral-300">
                                                    @{user.username}
                                                </span>
                                                <div className="relative shrink-0">
                                                    <select
                                                        value={user.permission}
                                                        disabled={shareUpdatingUser === user.id}
                                                        onChange={(event) => updateAccess(user.username, user.id, event.target.value as "view" | "edit")}
                                                        className="bs-vault-access-permission h-8 rounded-md border border-neutral-800 bg-black/65 pl-7 pr-2 text-xs font-semibold text-neutral-300 outline-none transition-colors hover:border-neutral-700 focus:border-cyan-700 disabled:opacity-60"
                                                        title={`Access for @${user.username}`}
                                                    >
                                                        <option value="view">Can view</option>
                                                        <option value="edit">Can edit</option>
                                                    </select>
                                                    <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2">
                                                        {shareUpdatingUser === user.id ? <Loader2 size={12} className="animate-spin text-neutral-500" /> : user.permission === "edit" ? <Edit3 size={12} className="text-cyan-300" /> : <Eye size={12} className="text-emerald-300" />}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeAccess(user.id)}
                                                    className="bs-vault-access-remove rounded-md px-2 py-1 text-xs text-rose-300 hover:bg-rose-950/30"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {note.isOwner !== false && toolsOpen && activeUtility === "share" && (
                            <div className="bs-vault-share-space-panel hidden-scrollbar max-h-[min(54vh,520px)] overflow-y-auto rounded-2xl bg-black/35 p-4 shadow-[inset_0_0_0_1px_rgba(34,211,238,0.16),0_0_34px_rgba(8,145,178,0.08)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                <div className="bs-vault-share-space-head mb-4 flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="bs-vault-share-space-title inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em] text-cyan-300">
                                            <ScrollText size={13} />
                                            Share to Space
                                        </div>
                                        <p className="mt-1 text-sm text-neutral-500">Send this note as a focused resource.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={shareMaterial}
                                        disabled={!materialSpaceId || materialSharing || spaceTargets.length === 0}
                                        className="bs-vault-share-space-submit inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-2xl bg-cyan-400/12 px-4 text-sm font-black text-cyan-100 shadow-[0_0_24px_rgba(8,145,178,0.16)] transition hover:bg-cyan-400/18 disabled:opacity-50"
                                    >
                                        {materialSharing ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                                        Share
                                    </button>
                                </div>

                                <label className="bs-vault-share-field block">
                                    <span className="bs-vault-share-label mb-2 block text-xs font-black tracking-[0.04em] text-neutral-500">Space</span>
                                    <select
                                        value={materialSpaceId}
                                        onChange={(event) => setMaterialSpaceId(event.target.value)}
                                        disabled={spaceTargetsLoading || spaceTargets.length === 0}
                                        className="bs-vault-share-select h-12 w-full rounded-2xl border border-white/10 bg-black/65 px-4 text-sm font-bold text-white outline-none focus:border-cyan-700 disabled:opacity-50"
                                    >
                                        {spaceTargetsLoading && <option>Loading spaces...</option>}
                                        {!spaceTargetsLoading && spaceTargets.length === 0 && <option>No joined spaces</option>}
                                        {spaceTargets.map((space) => (
                                            <option key={space.id} value={space.id}>
                                                {space.title}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <div className="bs-vault-share-kind mt-4">
                                    <span className="bs-vault-share-label mb-2 block text-xs font-black tracking-[0.04em] text-neutral-500">Kind</span>
                                    <div className="bs-vault-share-kind-row flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                        {MATERIAL_KINDS.map((kind) => {
                                            const active = materialKind === kind.id;
                                            const KindIcon = kind.icon;
                                            return (
                                                <button
                                                    key={kind.id}
                                                    type="button"
                                                    onClick={() => setMaterialKind(kind.id)}
                                                    className={`bs-vault-share-kind-chip group relative inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-2xl px-4 text-xs font-black transition ${active ? "is-active bg-cyan-400/12 text-cyan-100 shadow-[0_0_22px_rgba(34,211,238,0.18)]" : "bg-black/45 text-neutral-500 hover:text-neutral-200"}`}
                                                    aria-label={kind.label}
                                                    title={kind.label}
                                                >
                                                    <KindIcon size={15} />
                                                    <span className="bs-vault-share-kind-text">{kind.label}</span>
                                                    <span className="bs-vault-share-tooltip pointer-events-none absolute bottom-[calc(100%+0.45rem)] left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-neutral-950/95 px-2 py-1 text-[11px] font-bold text-neutral-200 shadow-2xl group-hover:block group-focus-visible:block">
                                                        {kind.label}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="bs-vault-share-options mt-4 grid gap-3 sm:grid-cols-[0.8fr_1fr]">
                                    <div className="bs-vault-share-sync rounded-2xl bg-black/45 p-1">
                                        {([
                                            { id: "frozen" as const, label: "Frozen", icon: ArchiveRestore },
                                            { id: "live" as const, label: "Live sync", icon: Sparkles },
                                        ]).map(({ id, label, icon: Icon }) => (
                                            <button
                                                key={id}
                                                type="button"
                                                onClick={() => setMaterialSyncMode(id)}
                                                className={`bs-vault-share-icon-toggle group relative inline-flex h-10 w-1/2 items-center justify-center gap-1.5 rounded-xl text-xs font-black transition ${materialSyncMode === id ? "is-active bg-emerald-400/12 text-emerald-100" : "text-neutral-500 hover:text-neutral-200"}`}
                                                aria-label={label}
                                                title={label}
                                            >
                                                <Icon size={15} />
                                                <span className="bs-vault-share-option-text">{label}</span>
                                                <span className="bs-vault-share-tooltip pointer-events-none absolute bottom-[calc(100%+0.45rem)] left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-neutral-950/95 px-2 py-1 text-[11px] font-bold text-neutral-200 shadow-2xl group-hover:block group-focus-visible:block">
                                                    {label}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="bs-vault-share-permissions grid grid-cols-3 gap-2">
                                        {[
                                            ["allowComments", "Comments", MessageSquareText],
                                            ["allowCopy", "Copy", Copy],
                                            ["allowRemix", "Save", ShieldCheck],
                                        ].map(([key, label, Icon]: any) => {
                                            const active = Boolean((materialPermissions as any)[key]);
                                            return (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    onClick={() => setMaterialPermissions((prev) => ({ ...prev, [key]: !Boolean((prev as any)[key]) }))}
                                                    className={`bs-vault-share-icon-toggle group relative flex h-10 items-center justify-center gap-1.5 rounded-2xl text-xs font-black transition ${active ? "is-active bg-violet-400/12 text-violet-100 shadow-[0_0_18px_rgba(168,85,247,0.14)]" : "bg-black/45 text-neutral-600 hover:text-neutral-300"}`}
                                                    title={String(label)}
                                                    aria-label={String(label)}
                                                >
                                                    <Icon size={14} />
                                                    <span className="bs-vault-share-option-text">{label}</span>
                                                    <span className="bs-vault-share-tooltip pointer-events-none absolute bottom-[calc(100%+0.45rem)] left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-neutral-950/95 px-2 py-1 text-[11px] font-bold text-neutral-200 shadow-2xl group-hover:block group-focus-visible:block">
                                                        {label}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </article>
    );
}

export function SidebarButton({
    active,
    icon: Icon,
    label,
    count,
    iconClassName = "text-neutral-500",
    activeClassName = "text-white",
    onClick,
}: {
    active: boolean;
    icon: LucideIcon;
    label: string;
    count?: number;
    iconClassName?: string;
    activeClassName?: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                active ? activeClassName : "text-neutral-400 hover:text-neutral-100"
            }`}
        >
            <Icon size={16} className={`${iconClassName} bs-vault-filter-icon ${active ? "is-active" : ""}`} />
            <span className="min-w-0 flex-1 truncate">{label}</span>
            {count !== undefined && <span className="text-xs text-neutral-600">{count}</span>}
        </button>
    );
}
